import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  LOCALE_ID,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  OrderFilters as OrderFiltersModel,
  OrderLookup,
} from '../../../../../pages/admin-dashboard/orders-management/orders.model';
import { FilterSortingComponent } from '../../../../../shared/ui/shared-filters/filter-sorting-component/filter-sorting-component';
import { FilterInput } from '../../../../../shared/ui/shared-filters/filter-input/filter-input';
import { FilterSelectComponent } from '../../../../../shared/ui/shared-filters/filter-select-component/filter-select-component';
import { FilterDateComponent } from '../../../../../shared/ui/shared-filters/filter-date-component/filter-date-component';
import { FilterProductSearch } from '../../../../../shared/ui/shared-filters/filter-product-search/filter-product-search';
import { PharmacyService } from '../../../../../services/pharmacy/pharmacy.service';
import { finalize } from 'rxjs';
import { OrderService } from '../../../../../services/orders/order.service';

@Component({
  selector: 'app-order-filters',
  standalone: true,
  imports: [
    FormsModule,
    FilterSortingComponent,
    FilterInput,
    FilterSelectComponent,
    FilterDateComponent,
    FilterProductSearch,
  ],
  templateUrl: './order-filters.html',
  styleUrl: './order-filters.scss',
})
export class OrderFilters implements OnInit {
  private locale = inject(LOCALE_ID);
  private destroyRef = inject(DestroyRef);
  private pharmacyService = inject(PharmacyService);
  private orderService = inject(OrderService);

  filters = input.required<OrderFiltersModel>();
  applyFilters = output<OrderFiltersModel>();
  toggleSortOrder = output();
  resetFilters = output();
  selectPharmacyOwner = output<{ id: number; fullName: string }>();

  orderId = signal<number | null>(null);
  statuses = signal<OrderLookup[]>([]);

  ownerSearchTerm = signal('');
  ownerResults = signal<{ id: number; titleAr: string; titleEn: string }[]>([]);
  ownerLoading = signal(false);
  ownerError = signal('');
  showOwnerDropdown = signal(false);

  selectedStatusId = signal<number | null>(null);

  invoiceAmountFrom = signal<number | null>(null);
  invoiceAmountTo = signal<number | null>(null);
  invoiceAmountErrorType = signal<'none' | 'invalid' | 'negative' | 'range'>('none');
  invoiceAmountInvalid = computed(() => this.invoiceAmountErrorType() !== 'none');

  invoiceAmountErrorMessage = computed(() => {
    const ar = this.isArabic();
    switch (this.invoiceAmountErrorType()) {
      case 'invalid':
        return ar ? 'يرجى إدخال رقم صحيح.' : 'Please enter a valid number.';
      case 'negative':
        return ar ? 'يجب أن يكون المبلغ رقماً موجباً.' : 'Amount must be a positive number.';
      case 'range':
        return ar
          ? 'الحد الأدنى للمبلغ لا يمكن أن يكون أكبر من الحد الأقصى.'
          : 'Minimum amount cannot be greater than maximum amount.';
      default:
        return '';
    }
  });

  createdOnFrom = signal<string | null>(null);
  createdOnTo = signal<string | null>(null);
  lastModifiedOnFrom = signal<string | null>(null);
  lastModifiedOnTo = signal<string | null>(null);

  createdDateInvalid = signal(false);
  lastModifiedDateInvalid = signal(false);

  isArabic = computed(() => this.locale.startsWith('ar'));

  statusOptions = computed(() =>
    this.statuses().map((s) => ({
      id: s.id,
      titleEn: s.titleEn,
      titleAr: s.titleAr,
    })),
  );

  sortOptions = computed(() => [
    { value: 'CREATION_DATE', labelEn: 'Creation Date', labelAr: 'تاريخ الإنشاء' },
    { value: 'LAST_MODIFICATION_DATE', labelEn: 'Last Modified', labelAr: 'آخر تعديل' },
    { value: 'ORDER_ID', labelEn: 'Order ID', labelAr: 'رقم الطلب' },
    { value: 'TOTAL_ITEMS', labelEn: 'Total Items', labelAr: 'إجمالي العناصر' },
    { value: 'TOTAL_PRICE', labelEn: 'Total Price', labelAr: 'إجمالي السعر' },
  ]);

  constructor() {
    effect(() => {
      this.validateInvoiceAmount();
    });
  }

  ngOnInit(): void {
    this.loadStatuses();
  }

  loadStatuses() {
    const subscription = this.orderService.getOrderStatuses().subscribe({
      next: (res) => this.statuses.set(res.list),
      error: () => {},
    });
    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }

  onOwnerSearchChange(value: string) {
    this.ownerSearchTerm.set(value);
    if (!value) {
      this.showOwnerDropdown.set(false);
      this.ownerResults.set([]);
      return;
    }
    this.ownerLoading.set(true);
    this.showOwnerDropdown.set(true);
    const subscription = this.pharmacyService
      .pharmacyOwnerAutoComplete(value)
      .pipe(finalize(() => this.ownerLoading.set(false)))
      .subscribe({
        next: (res) =>
          this.ownerResults.set(
            (res.list as any[]).map((i) => ({ id: i.id, titleAr: i.fullName, titleEn: i.fullName })),
          ),
        error: () => this.ownerError.set(this.isArabic() ? 'حدث خطأ' : 'Error fetching results'),
      });
    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }

  onOwnerSelect(item: { id: number; titleAr: string; titleEn: string }) {
    this.selectPharmacyOwner.emit({ id: item.id, fullName: item.titleEn });
    this.showOwnerDropdown.set(false);
  }

  onContainerClick(event: Event) {
    event.stopPropagation();
  }

  closeAllDropdowns() {
    this.showOwnerDropdown.set(false);
  }

  onToggleSortOrder() {
    this.toggleSortOrder.emit();
  }

  private validateInvoiceAmount(): boolean {
    const min = this.invoiceAmountFrom();
    const max = this.invoiceAmountTo();

    if ((min !== null && isNaN(min)) || (max !== null && isNaN(max))) {
      this.invoiceAmountErrorType.set('invalid');
      return false;
    }

    if ((min !== null && min < 0) || (max !== null && max < 0)) {
      this.invoiceAmountErrorType.set('negative');
      return false;
    }

    if (min !== null && max !== null && min > max) {
      this.invoiceAmountErrorType.set('range');
      return false;
    }

    this.invoiceAmountErrorType.set('none');
    return true;
  }

  onResetFilters() {
    this.orderId.set(null);
    this.ownerSearchTerm.set('');
    this.ownerResults.set([]);
    this.selectedStatusId.set(null);
    this.invoiceAmountFrom.set(null);
    this.invoiceAmountTo.set(null);
    this.invoiceAmountErrorType.set('none');
    this.createdOnFrom.set(null);
    this.createdOnTo.set(null);
    this.lastModifiedOnFrom.set(null);
    this.lastModifiedOnTo.set(null);
    this.createdDateInvalid.set(false);
    this.lastModifiedDateInvalid.set(false);
    this.closeAllDropdowns();
    this.resetFilters.emit();
  }

  onApplyFilters() {
    const createdInvalid = !!(
      this.createdOnFrom() &&
      this.createdOnTo() &&
      this.createdOnFrom()! > this.createdOnTo()!
    );
    const lastModInvalid = !!(
      this.lastModifiedOnFrom() &&
      this.lastModifiedOnTo() &&
      this.lastModifiedOnFrom()! > this.lastModifiedOnTo()!
    );

    const invoiceValid = this.validateInvoiceAmount();

    this.createdDateInvalid.set(createdInvalid);
    this.lastModifiedDateInvalid.set(lastModInvalid);

    if (createdInvalid || lastModInvalid || !invoiceValid) return;

    this.applyFilters.emit({
      orderId: this.orderId() ?? undefined,
      pharmacyOwnerId: this.ownerSearchTerm() ? (this.filters().pharmacyOwnerId ?? undefined) : undefined,
      requestStatusId: this.selectedStatusId() ?? undefined,
      invoiceAmountFrom: this.invoiceAmountFrom() ?? undefined,
      invoiceAmountTo: this.invoiceAmountTo() ?? undefined,
      createdOnFrom: this.createdOnFrom() ?? undefined,
      createdOnTo: this.createdOnTo() ?? undefined,
      lastModifiedOnFrom: this.lastModifiedOnFrom() ?? undefined,
      lastModifiedOnTo: this.lastModifiedOnTo() ?? undefined,
    });
  }
}
